// Painel de adesão: registro de eventos de uso (sessão/atividade) e leitura do
// relatório. Coleta mínima (LGPD): quem, quando, tipo de evento e a seção do app
// (1º trecho da rota). Nunca conteúdo de telas, textos ou ids de projetos.
// Autorização real no banco (RLS de usage_events + admin_usage_report) — ver 0031.
import { supabase } from './supabase';

export const HEARTBEAT_MS = 60_000;
/** Sem interação por este tempo, o pulso deixa de ser enviado (timeout de inatividade). */
export const IDLE_MS = 5 * 60_000;

export interface UsageRow {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  last_access: string | null;
  last_sign_in: string | null;
  sessions: number;
  active_days: number;
  minutes_logged: number;
  actions_total: number;
  actions_stages: number;
  actions_notes: number;
  actions_gates: number;
  actions_messages: number;
  last_action: string | null;
}

export async function sendUsageEvent(
  orgId: string,
  userId: string,
  sessionId: string,
  kind: 'open' | 'heartbeat',
  section: string,
): Promise<boolean> {
  const { error } = await supabase.from('usage_events').insert({
    org_id: orgId,
    user_id: userId,
    session_id: sessionId,
    kind,
    section: section.slice(0, 40),
  });
  return !error;
}

export async function loadUsageReport(
  orgId: string,
  days: number,
  idleMinutes = IDLE_MS / 60_000,
): Promise<UsageRow[]> {
  const { data, error } = await supabase.rpc('admin_usage_report', {
    p_org: orgId,
    p_days: days,
    p_idle_minutes: idleMinutes,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as UsageRow[]).map((r) => ({
    ...r,
    minutes_logged: Number(r.minutes_logged),
  }));
}

/** Seção do app a partir da rota ("/produto/abc" → "produto"). */
export function sectionOf(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0];
  return seg ?? 'inicio';
}

export type UsageStatus = 'nunca' | 'inativo' | 'ativo';

/** Nunca acessou (sem eventos nem login registrado) / inativo há ≥ N dias / ativo. */
export function usageStatus(r: UsageRow, inactiveDays: number, now = Date.now()): UsageStatus {
  const last = [r.last_access, r.last_sign_in]
    .filter((x): x is string => Boolean(x))
    .map((x) => Date.parse(x))
    .reduce((a, b) => Math.max(a, b), 0);
  if (!last) return 'nunca';
  return now - last >= inactiveDays * 86_400_000 ? 'inativo' : 'ativo';
}

export function fmtMinutes(min: number): string {
  if (!min) return '0 min';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
}

function csvCell(v: string | number | null): string {
  const s = v == null ? '' : String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** CSV (separador ";", com BOM) para abrir direto no Excel em pt-BR. */
export function usageToCsv(rows: UsageRow[], inactiveDays: number, days: number): string {
  const head = [
    'Nome',
    'E-mail',
    'Papel',
    'Situação',
    'Último acesso',
    `Sessões (${days} dias)`,
    'Dias com uso',
    'Tempo logado (min)',
    'Ações (total)',
    'Etapas/projetos',
    'Notas',
    'Aprovações de gate',
    'Mensagens',
    'Última ação',
  ];
  const label: Record<UsageStatus, string> = {
    nunca: 'Nunca acessou',
    inativo: `Inativo há ${inactiveDays}+ dias`,
    ativo: 'Ativo',
  };
  const lines = rows.map((r) =>
    [
      r.full_name ?? '',
      r.email,
      r.role,
      label[usageStatus(r, inactiveDays)],
      r.last_access ?? r.last_sign_in ?? '',
      r.sessions,
      r.active_days,
      r.minutes_logged,
      r.actions_total,
      r.actions_stages,
      r.actions_notes,
      r.actions_gates,
      r.actions_messages,
      r.last_action ?? '',
    ]
      .map(csvCell)
      .join(';'),
  );
  return '﻿' + [head.map(csvCell).join(';'), ...lines].join('\r\n');
}
